import { useState } from "react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { PROJECTS } from "@/data/content";
import { MapPin, Calendar, Briefcase, CheckCircle2, X } from "lucide-react";

const FILTERS = ["All", "Operational", "Under Execution"];

export default function Projects() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const list = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.status === filter);

  return (
    <div data-testid="projects-page">
      <PageHero
        overline="Project Portfolio"
        title="Megawatts delivered. Trust earned."
        subtitle="A selection of utility-scale, government and captive solar projects executed by VPCL Infra across India."
        image="https://images.unsplash.com/photo-1629726797843-618688139f5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        testid="projects-hero"
      />

      <section className="section-padding bg-white">
        <div className="container-corp">
          <div className="flex flex-wrap gap-3 mb-12" data-testid="projects-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`text-xs uppercase tracking-[0.18em] px-5 py-2.5 border transition-colors ${
                  filter === f
                    ? "bg-[#0B2341] text-white border-[#0B2341]"
                    : "bg-white text-[#0B2341] border-[#E2E8F0] hover:border-[#0B2341]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((p) => (
              <button
                key={p.slug}
                data-testid={`project-${p.slug}`}
                onClick={() => setActive(p)}
                className="group text-left bg-white border border-[#E2E8F0] hover:border-[#0B2341]/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-4 left-4 bg-[#16A34A] text-[#0B2341] text-xs font-bold uppercase tracking-wider px-3 py-1.5">
                    {p.capacity}
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-medium text-[#0B2341]">
                    {p.status}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-[#0B2341] mb-3 group-hover:text-[#16A34A] transition-colors">
                    {p.name}
                  </h3>
                  <div className="space-y-1.5 text-xs text-[#4A5568]">
                    <div className="flex items-center gap-2"><MapPin size={12} className="text-[#16A34A]"/> {p.location}</div>
                    <div className="flex items-center gap-2"><Briefcase size={12} className="text-[#16A34A]"/> {p.client}</div>
                    <div className="flex items-center gap-2"><Calendar size={12} className="text-[#16A34A]"/> {p.completion}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* MODAL */}
      {active && (
        <div
          data-testid="project-modal"
          className="fixed inset-0 z-[60] bg-[#0B2341]/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
          onClick={() => setActive(null)}
        >
          <div
            className="bg-white w-full max-w-4xl my-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="modal-close"
              onClick={() => setActive(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white border border-[#E2E8F0] flex items-center justify-center hover:bg-[#0B2341] hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="relative aspect-[16/9]">
              <img src={active.image} alt={active.name} className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 bg-[#16A34A] text-[#0B2341] text-sm font-bold uppercase tracking-wider px-4 py-2">
                {active.capacity}
              </div>
            </div>
            <div className="p-8 md:p-12">
              <div className="overline mb-3">Case Study</div>
              <h2 className="font-heading text-3xl font-semibold text-[#0B2341] mb-6">
                {active.name}
              </h2>
              <p className="text-[#4A5568] leading-relaxed mb-8">{active.summary}</p>

              <dl className="grid sm:grid-cols-2 gap-y-5 gap-x-10 mb-10 border-y border-[#E2E8F0] py-6">
                {[
                  ["Capacity", active.capacity],
                  ["Location", active.location],
                  ["Client", active.client],
                  ["Scope", active.scope],
                  ["Completion", active.completion],
                  ["Status", active.status],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] uppercase tracking-[0.22em] text-[#4A5568] mb-1">{k}</dt>
                    <dd className="text-[#0B2341] font-medium">{v}</dd>
                  </div>
                ))}
              </dl>

              <h3 className="font-heading text-lg font-semibold text-[#0B2341] mb-4">Project Highlights</h3>
              <ul className="space-y-3">
                {active.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#0A1118]">
                    <CheckCircle2 size={18} className="text-[#16A34A] mt-0.5 flex-shrink-0" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <CTASection
        overline="Reference our delivery"
        title="Want a project deep-dive?"
        body="Request our investor-grade case study deck with drone footage, single-line diagrams and CUF performance reports."
      />
    </div>
  );
}
